import { useState, useEffect } from "react";
import { toast } from "sonner";
import { adminSettings, adminSaveSettings, adminCustomers } from "../../lib/api";

const dark = "w-full bg-transparent border border-[#26241f] px-4 py-3 text-[#f4f2ec] placeholder:text-[#5c584f] focus:border-gold focus:outline-none transition-colors text-sm";
const lbl = "block text-[0.6rem] uppercase tracking-[0.18em] text-[#9a968c] mb-2";

export const SettingsTab = ({ token }) => {
  const [form, setForm] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    adminSettings(token).then((r) => setForm({ ...r.data, free_shipping_over: r.data.free_shipping_over ?? "" })).catch(() => toast.error("Failed to load settings."));
    adminCustomers(token).then((r) => setCustomers(r.data)).catch(() => {});
  }, [token]);

  if (!form) return <p className="text-[#9a968c]">Loading settings…</p>;

  const save = async () => {
    setSaving(true);
    try {
      await adminSaveSettings({
        ...form,
        delivery_fee: Number(form.delivery_fee) || 0,
        free_shipping_over: form.free_shipping_over === "" ? null : Number(form.free_shipping_over),
      }, token);
      toast.success("Store settings saved.");
    } catch { toast.error("Save failed."); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="settings-tab">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div className="border border-[#1a1814] p-8">
          <h3 className="font-serif text-2xl text-[#f4f2ec]">Delivery & store</h3>
          <p className="text-sm text-[#9a968c] mt-2">Applies to every shop order at checkout.</p>
          <div className="space-y-5 mt-7">
            <div>
              <label className={lbl}>Flat delivery fee (AUD, per order)</label>
              <input className={dark} value={form.delivery_fee} onChange={(e) => set("delivery_fee", e.target.value)} data-testid="setting-delivery-fee" />
            </div>
            <div>
              <label className={lbl}>Free delivery over (blank = never)</label>
              <input className={dark} value={form.free_shipping_over} onChange={(e) => set("free_shipping_over", e.target.value)} data-testid="setting-free-over" />
            </div>
            <div>
              <label className={lbl}>Store name</label>
              <input className={dark} value={form.store_name} onChange={(e) => set("store_name", e.target.value)} data-testid="setting-store-name" />
            </div>
            <div>
              <label className={lbl}>Shop notice (optional banner text)</label>
              <input className={dark} value={form.shop_notice || ""} onChange={(e) => set("shop_notice", e.target.value)} data-testid="setting-notice" />
            </div>
            <button onClick={save} disabled={saving} data-testid="settings-save"
              className="w-full px-6 py-4 bg-gold text-[#050505] text-xs uppercase tracking-[0.2em] hover:bg-[#e6c56a] transition-colors disabled:opacity-60">
              {saving ? "Saving…" : "Save settings"}
            </button>
          </div>
        </div>

        <div className="border border-[#1a1814] p-8">
          <h3 className="font-serif text-2xl text-[#f4f2ec]">Customers</h3>
          <p className="text-sm text-[#9a968c] mt-2">{customers.length} account{customers.length === 1 ? "" : "s"} created at checkout.</p>
          <div className="mt-7 divide-y divide-[#12110e]" data-testid="customers-list">
            {customers.length === 0 ? (
              <p className="text-sm text-[#6f6c64]">No customer accounts yet.</p>
            ) : customers.slice(0, 25).map((c) => (
              <div key={c.id} className="py-3 flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm text-[#f4f2ec]">{c.name}</p>
                  <a href={`mailto:${c.email}`} className="text-xs text-gold hover:underline">{c.email}</a>
                </div>
                <span className="text-xs text-[#6f6c64] shrink-0">{new Date(c.created_at).toLocaleDateString("en-AU")}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
